import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import { enableMapSet } from 'immer';

// This store keeps several fields as Map (optimisticTasks, conflictTasks,
// taskVersions, optimisticComments, conflictComments) and mutates them
// inside immer's producer via zustand/middleware/immer. Immer does not
// support Map/Set by default — without this, any state update touching a
// Map (e.g. every incoming task.update WebSocket event, since updateTask
// runs through this store) throws "[Immer] The plugin for 'MapSet' has
// not been loaded" and is silently swallowed by the WebSocket message
// handler's try/catch, making real-time task sync appear to just not
// work. Confirmed via live reproduction (Release Hardening Session 3).
enableMapSet();

export const useRealtimeStore = create()(
  immer((set, get) => ({
    // Tasks state
    tasks: [],
    optimisticTasks: new Map(), // task_id -> optimistic_data
    conflictTasks: new Map(), // task_id -> conflict_data
    taskVersions: new Map(), // task_id -> version

    // Comments state
    comments: [],
    optimisticComments: new Map(),
    conflictComments: new Map(),

    // Actions for tasks
    setTasks: (tasks) => set((state) => {
      state.tasks = Array.isArray(tasks) ? tasks : typeof tasks === 'function' ? tasks(state.tasks) : [];
    }),

    addTask: (task) => set((state) => {
      const existingIndex = state.tasks.findIndex(t => t.id === task.id);
      if (existingIndex >= 0) {
        state.tasks[existingIndex] = task;
      } else {
        state.tasks.push(task);
      }
      state.taskVersions.set(task.id, task.version || 1);
    }),

    updateTask: (taskUpdate) => set((state) => {
      const existingIndex = state.tasks.findIndex(t => t.id === taskUpdate.id);
      if (existingIndex >= 0) {
        state.tasks[existingIndex] = { ...state.tasks[existingIndex], ...taskUpdate };
        if (taskUpdate.version) {
          state.taskVersions.set(taskUpdate.id, taskUpdate.version);
        }
      } else if (taskUpdate.id) {
        // New task from real-time event
        state.tasks.push(taskUpdate);
        state.taskVersions.set(taskUpdate.id, taskUpdate.version || 1);
      }
    }),

    moveTask: (taskId, toStatus, position) => set((state) => {
      const taskIndex = state.tasks.findIndex(t => t.id === taskId);
      if (taskIndex >= 0) {
        state.tasks[taskIndex].status = toStatus;
        if (position !== undefined) {
          state.tasks[taskIndex].position = position;
        }
      }
    }),

    reorderTasks: (status, taskIds) => set((state) => {
      // Update positions based on new order
      taskIds.forEach((taskId, index) => {
        const taskIndex = state.tasks.findIndex(t => t.id === taskId && t.status === status);
        if (taskIndex >= 0) {
          state.tasks[taskIndex].position = index;
        }
      });
      
      // Sort tasks by position within each status
      state.tasks.sort((a, b) => {
        if (a.status !== b.status) return 0;
        return (a.position || 0) - (b.position || 0);
      });
    }),

    deleteTask: (taskId) => set((state) => {
      state.tasks = state.tasks.filter(t => t.id !== taskId);
      state.optimisticTasks.delete(taskId);
      state.conflictTasks.delete(taskId);
      state.taskVersions.delete(taskId);
    }),

    // Optimistic updates
    addOptimisticTask: (taskId, optimisticData) => set((state) => {
      state.optimisticTasks.set(taskId, optimisticData);
      // Apply optimistic update to tasks array
      const existingIndex = state.tasks.findIndex(t => t.id === taskId);
      if (existingIndex >= 0) {
        state.tasks[existingIndex] = { ...state.tasks[existingIndex], ...optimisticData };
      }
    }),

    removeOptimisticTask: (taskId) => set((state) => {
      state.optimisticTasks.delete(taskId);
    }),

    rollbackTask: (taskId) => set((state) => {
      const optimisticData = state.optimisticTasks.get(taskId);
      if (optimisticData) {
        // Revert task to previous state
        const taskIndex = state.tasks.findIndex(t => t.id === taskId);
        if (taskIndex >= 0) {
          // Remove optimistic changes - would need to store original state
          // For now, just remove from optimistic map
        }
        state.optimisticTasks.delete(taskId);
      }
    }),

    updateTaskVersion: (taskId, version) => set((state) => {
      state.taskVersions.set(taskId, version);
    }),

    // Conflict resolution
    addConflict: (entityId, conflictData) => set((state) => {
      if (conflictData.entity_type === 'task') {
        state.conflictTasks.set(entityId, conflictData);
      } else if (conflictData.entity_type === 'comment') {
        state.conflictComments.set(entityId, conflictData);
      }
    }),

    resolveConflict: (entityId, entityType, resolution = 'accept_server') => set((state) => {
      if (entityType === 'task') {
        const conflictData = state.conflictTasks.get(entityId);
        if (conflictData && resolution === 'accept_server') {
          // Accept server version
          const taskIndex = state.tasks.findIndex(t => t.id === entityId);
          if (taskIndex >= 0) {
            state.tasks[taskIndex] = conflictData.server_data;
            state.taskVersions.set(entityId, conflictData.server_version);
          }
        }
        state.conflictTasks.delete(entityId);
        state.optimisticTasks.delete(entityId);
      } else if (entityType === 'comment') {
        const conflictData = state.conflictComments.get(entityId);
        if (conflictData && resolution === 'accept_server') {
          // Accept server version for comments
          const commentIndex = state.comments.findIndex(c => c.id === entityId);
          if (commentIndex >= 0) {
            state.comments[commentIndex] = conflictData.server_data;
          }
        }
        state.conflictComments.delete(entityId);
        state.optimisticComments.delete(entityId);
      }
    }),

    // Comments actions
    setComments: (comments) => set((state) => {
      state.comments = Array.isArray(comments) ? comments : [];
    }),

    addComment: (comment) => set((state) => {
      const existingIndex = state.comments.findIndex(c => c.id === comment.id);
      if (existingIndex >= 0) {
        state.comments[existingIndex] = comment;
      } else {
        state.comments.push(comment);
      }
    }),

    updateComment: (commentUpdate) => set((state) => {
      const existingIndex = state.comments.findIndex(c => c.id === commentUpdate.id);
      if (existingIndex >= 0) {
        state.comments[existingIndex] = { ...state.comments[existingIndex], ...commentUpdate };
      }
    }),

    deleteComment: (commentId) => set((state) => {
      state.comments = state.comments.filter(c => c.id !== commentId);
      state.optimisticComments.delete(commentId);
      state.conflictComments.delete(commentId);
    }),

    // Optimistic comments
    addOptimisticComment: (commentId, optimisticData) => set((state) => {
      state.optimisticComments.set(commentId, optimisticData);
      const existingIndex = state.comments.findIndex(c => c.id === commentId);
      if (existingIndex >= 0) {
        state.comments[existingIndex] = { ...state.comments[existingIndex], ...optimisticData };
      }
    }),

    removeOptimisticComment: (commentId) => set((state) => {
      state.optimisticComments.delete(commentId);
    }),

    // Utility functions
    getTaskById: (taskId) => {
      const state = get();
      return state.tasks.find(t => t.id === taskId);
    },

    getTasksByStatus: (status) => {
      const state = get();
      return state.tasks
        .filter(t => t.status === status)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    },

    isTaskOptimistic: (taskId) => {
      const state = get();
      return state.optimisticTasks.has(taskId);
    },

    isTaskInConflict: (taskId) => {
      const state = get();
      return state.conflictTasks.has(taskId);
    },

    getTaskVersion: (taskId) => {
      const state = get();
      return state.taskVersions.get(taskId) || 1;
    },

    // Reset all state (for cleanup)
    reset: () => set((state) => {
      state.tasks = [];
      state.comments = [];
      state.optimisticTasks.clear();
      state.optimisticComments.clear();
      state.conflictTasks.clear();
      state.conflictComments.clear();
      state.taskVersions.clear();
    })
  }))
);